import { Button } from "@/components/ui/Button";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { createCheckoutSession } from "@/features/billing/api/billing";
import { usePlanCatalog } from "@/features/pricing/hooks/usePlanCatalog";
import { useUsage } from "@/features/usage/hooks/useUsage";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { isProPlan } from "@/utils/plan";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

type DisplayPlan = {
  id: string;
  name: string;
  price: string;
  cadence?: string;
  documents: string;
  greps: string;
  searches: string;
  sql?: string;
  grepCap?: string;
  queue: string;
  datasets: string;
  datasetRows: string;
  cta?: string;
  href?: string;
};

const DISPLAY_PLANS = ["beta_free", "pro"];

/** Datasets each plan builds and the rows it shows of each, as the backend's plans give them. */
const DATASETS: Record<string, { datasets: string; datasetRows: string }> = {
  beta_free: { datasets: "5 total", datasetRows: "50" },
  pro: { datasets: "20 / month", datasetRows: "200" },
};

const usePrices = () => {
  const query = usePlanCatalog();

  const plans = useMemo((): DisplayPlan[] | null => {
    if (!query.data) return null;
    return query.data.plans
      .filter((plan) => DISPLAY_PLANS.includes(plan.id))
      .map(({
        id,
        name,
        price,
        limits: {
          dailyMaps
        },
        capabilities: {
          heavyGrepStartsPerMinute,
          heavySearchStartsPerMinute,
          heavyQueuePriority,
        },
      }) => {
        return {
          id,
          name: !/free/i.test(name) ? name + " Plan" : name,
          // Prices as seen in dev or staging may not reflect prices as seen in prod. This data is pulled from Stripe.
          price: !price ? "$0" : `$${(price.amount / 100).toLocaleString()}`,
          cadence: price?.cadence ? `/ ${price.cadence}` : "/ month",
          documents: dailyMaps.isUnlimited ? "Unlimited" : dailyMaps.total.toLocaleString(),
          greps: heavyGrepStartsPerMinute.isUnlimited ? "Unlimited" : heavyGrepStartsPerMinute.total.toLocaleString(),
          searches: heavySearchStartsPerMinute.isUnlimited ? "Unlimited" : heavySearchStartsPerMinute.total.toLocaleString(),
          queue: heavyQueuePriority.tier < 2 ? "Priority" : "Standard",
          ...(DATASETS[id] ?? { datasets: "", datasetRows: "" }),
          cta: id === "pro" ? "Get Pro plan" : undefined,
          href: id === "pro" ? "/login" : undefined,
        };
      });
  }, [query.data]);

  return { plans, isPending: query.isPending, isError: query.isError };
}

type RowKey = keyof Pick<DisplayPlan, "documents" | "greps" | "searches" | "sql" | "grepCap" | "queue" | "datasets" | "datasetRows">;

const Cmd = ({ children }: { children: ReactNode }) => {
  return <code className="font-mono text-sm text-zinc-600">{children}</code>;
}

const ROWS: Array<{ label: ReactNode; key: RowKey }> = [
  {
    label: (
      <>
        <Cmd>map</Cmd> documents / day
      </>
    ),
    key: "documents",
  },
  {
    label: (
      <>
        Corpus-wide <Cmd>grep</Cmd> / min
      </>
    ),
    key: "greps",
  },
  {
    label: (
      <>
        <Cmd>search</Cmd> / min
      </>
    ),
    key: "searches",
  },
  { label: "Command queue", key: "queue" },
  { label: "Datasets", key: "datasets" },
  { label: "Rows shown per dataset", key: "datasetRows" },
];

const CTA_LINK =
  "inline-block text-sm text-zinc-900 underline decoration-zinc-300 underline-offset-4 transition-colors hover:decoration-zinc-700";

const CTA_BUTTON =
  "inline-block rounded-[3px] bg-zinc-900 px-3 py-1 text-sm leading-6 text-white transition-opacity hover:opacity-90 cursor-pointer";

const CTA_LABEL =
  "inline-block rounded-[3px] bg-zinc-900 px-3 py-1 text-sm leading-6 text-white";

const Bar = ({ className }: { className: string }) => (
  <div className={`animate-pulse rounded-[3px] bg-zinc-100 ${className}`} />
);

const PlanTableSkeleton = () => (
  <table className="w-full table-fixed border-collapse text-left" aria-busy="true">
    <caption className="sr-only">Loading pricing plans</caption>
    <thead>
      <tr className="align-bottom">
        <th scope="col" className="w-[44%] pb-4" aria-hidden />
        {DISPLAY_PLANS.map((id) => (
          <th key={id} scope="col" className="pb-4 pr-4 font-normal">
            <Bar className="h-7 w-28" />
            <Bar className="mt-2 h-5 w-16" />
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {ROWS.map((row) => (
        <tr key={row.key} className="border-t border-zinc-200/70">
          <th scope="row" className="py-2.5 pr-4">
            <Bar className="h-4 w-36" />
          </th>
          {DISPLAY_PLANS.map((id) => (
            <td key={id} className="py-2.5 pr-4">
              <Bar className="h-4 w-12" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const Cta = ({ plan }: { plan: DisplayPlan }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: usageData, isLoading } = useUsage();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const isPro = useMemo(() => isProPlan(usageData?.planId), [usageData]);

  const handleUpgrade = useCallback(async () => {
    setCheckoutLoading(true);
    try {
      const { checkout_url } = await createCheckoutSession();
      window.location.href = checkout_url;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to start checkout.";
      toast.error(message);
    } finally {
      setCheckoutLoading(false);
    }
  }, []);

  if (!plan.cta || isLoading)
    return null

  if (!user) {
    return (
      <Button
        className={CTA_BUTTON}
        onClick={() => navigate("/login")}
      >
        Signin to upgrade
      </Button>
    )
  }

  return !isPro ? (
    <Button
      className={CTA_BUTTON}
      loading={checkoutLoading}
      onClick={handleUpgrade}
    >
      {plan.cta}
    </Button>) : (
    <div className={CTA_LABEL}>
      Current Plan
    </div>
  )
}

export const PricingPage = () => {
  useDocumentTitle("Pricing");

  const { plans, isPending, isError } = usePrices();

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl px-8 py-8">
        <header className="mb-10">
          <h1 className="font-display text-4xl leading-none tracking-tight text-zinc-900">Pricing</h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-600">
            Paperclip comes with generous access without cost, but many users have expressed the
            need for increased rate limits. To fulfill this demand, we are providing a Pro plan
            with significantly higher rate limits compared to the Free plan.
          </p>
        </header>

        {/* Plan table: one column per plan, a row per limit. */}
        <section aria-labelledby="plans-heading" className="rounded-[3px] border border-zinc-900/[0.14] px-6 py-5">
          <h2 id="plans-heading" className="sr-only">
            Pricing plans
          </h2>
          {isPending || !plans ? (
            isError ? (
              <p role="alert" className="text-sm leading-6 text-zinc-600">
                Pricing is unavailable right now. Refresh the page to try again.
              </p>
            ) : (
              <PlanTableSkeleton />
            )
          ) : (
            <table className="w-full table-fixed border-collapse text-left">
              <thead>
                <tr className="align-bottom">
                  <th scope="col" className="w-[44%] pb-4" aria-hidden />
                  {plans.map((plan) => (
                    <th key={plan.id} scope="col" className="pb-4 pr-4 font-normal">
                      <p className="font-display text-2xl leading-none tracking-tight text-zinc-900">
                        {plan.name}
                      </p>
                      <p className="mt-2 text-base leading-6 text-zinc-900">
                        <span className="tabular-nums">{plan.price}</span>
                        {plan.cadence && <span className="text-zinc-400"> {plan.cadence}</span>}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.key} className="border-t border-zinc-200/70">
                    <th scope="row" className="py-2.5 pr-4 text-sm font-normal leading-6 text-zinc-500">
                      {row.label}
                    </th>
                    {plans.map((plan) => (
                      <td key={plan.name} className="py-2.5 pr-4 text-base leading-6 tabular-nums text-zinc-900">
                        {plan[row.key]}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="border-t border-zinc-300">
                  <td className="pt-5" aria-hidden />
                  {plans.map((plan) => (
                    <td key={plan.name} className="pr-4 pt-5">
                      <Cta plan={plan} />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          )}
        </section>

        <p className="mt-8 text-base leading-7 text-zinc-600">
          Need more usage, or every row of every dataset?{" "}
          <a href="mailto:learn@gxl.ai?subject=Paperclip%20Enterprise" className={CTA_LINK}>
            Contact us for Enterprise
          </a>
          .
        </p>
      </div>
    </div>
  );
};
